import { useRef, type ReactNode } from 'react';
import type { IconType } from 'react-icons';
import { Box, Flex, Icon, Text, VStack } from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { MdAdd, MdExpandMore, MdInfoOutline, MdSearch, MdSettings } from 'react-icons/md';
import { useAdminPendingJoinRequests } from '../../features/teams/hooks/useAdminPendingJoinRequests';
import { useUserTeam } from '../../features/teams/hooks/useUserTeam';
import { canAccessTeamSettings } from '../../features/teams/utils/teamPermissions';
import { useAuth } from '../../hooks/useAuth';
import { RiftboundPlayerAvatar } from '../RiftboundPlayerAvatar';
import { SlugText } from '../SlugText';
import { RiftboundButton } from '../ui/button';
import { menuItemHoverBg, menuItemSelectedBg, menuItemSelectedHoverBg } from '../../theme/recipes/button';
import { dropdownBg, raisedShadow } from '../../theme/themeColor';
import { MobileFilterFabBadge } from './MobileFilterFabBadge';
import { NavSubmenuPortal, useNavSubmenuHover } from './NavSubmenuPortal';

function SubmenuItemFrame({
  active,
  children,
}: {
  active: boolean;
  children: ReactNode;
}) {
  return (
    <Flex
      role="menuitem"
      align="center"
      gap={2.5}
      px={3}
      py={2}
      fontSize="sm"
      fontWeight={active ? 'semibold' : 'medium'}
      color={active ? 'accent' : 'text'}
      bg={active ? menuItemSelectedBg : 'transparent'}
      transitionProperty="background-color, color"
      transitionDuration="0.12s"
      _hover={{
        bg: active ? menuItemSelectedHoverBg : menuItemHoverBg,
        color: active ? 'accent' : 'text',
      }}
    >
      {children}
    </Flex>
  );
}

function TeamSubmenuLink({
  to,
  icon,
  active,
  children,
}: {
  to: string;
  icon: IconType;
  active: boolean;
  children: string;
}) {
  return (
    <RouterLink to={to} style={{ display: 'block', textDecoration: 'none', outline: 'none' }}>
      <SubmenuItemFrame active={active}>
        <Icon as={icon} boxSize={4} color={active ? 'accent' : 'textMuted'} flexShrink={0} aria-hidden />
        {children}
      </SubmenuItemFrame>
    </RouterLink>
  );
}

function MyTeamSubmenuLink({
  to,
  name,
  slug,
  avatarUrl,
  active,
}: {
  to: string;
  name: string;
  slug: string;
  avatarUrl: string | null;
  active: boolean;
}) {
  return (
    <RouterLink to={to} style={{ display: 'block', textDecoration: 'none', outline: 'none' }}>
      <SubmenuItemFrame active={active}>
        <RiftboundPlayerAvatar name={name} photoURL={avatarUrl} size="xs" flexShrink={0} />
        <VStack align="start" gap={0} minW={0} flex={1}>
          <Text fontSize="sm" fontWeight={active ? 'semibold' : 'medium'} lineClamp={1}>
            {name}
          </Text>
          <SlugText slug={slug} kind="team" fontSize="2xs" color={active ? 'accent' : 'textMuted'} />
        </VStack>
      </SubmenuItemFrame>
    </RouterLink>
  );
}

export interface TeamNavSubmenuProps {
  navMatch: (pathname: string) => boolean;
}

/** Desktop nav: Team trigger + my team / settings / directory / create. */
export function TeamNavSubmenu({ navMatch }: TeamNavSubmenuProps) {
  const location = useLocation();
  const { user } = useAuth();
  const hover = useNavSubmenuHover();
  const anchorRef = useRef<HTMLDivElement>(null);
  const { team } = useUserTeam(user?.teamId);
  const { count: pendingJoinRequestCount } = useAdminPendingJoinRequests();

  const pathname = location.pathname;
  const sectionActive = navMatch(pathname);
  const teamSlug = team?.slug ?? null;
  const myRole = team?.members.find((m) => m.uid === user?.uid)?.role;
  const canManage = canAccessTeamSettings(myRole);
  const hubActive = pathname === '/team';
  const myTeamActive = Boolean(teamSlug) && pathname.startsWith(`/teams/${teamSlug}`);
  const landingActive = pathname === '/features/teams';
  const badge = pendingJoinRequestCount;

  return (
    <Box
      ref={anchorRef}
      position="relative"
      onMouseEnter={hover.show}
      onMouseLeave={hover.hide}
    >
      <RiftboundButton
        as={RouterLink as never}
        {...{ to: '/team' } as Record<string, unknown>}
        treatment="ghost"
        size="sm"
        variant={sectionActive ? 'primary' : undefined}
        px={{ md: 2, xl: 3 }}
        h="auto"
        py={1.5}
        position="relative"
        aria-haspopup="menu"
        aria-expanded={hover.open}
        aria-label={
          badge > 0
            ? `Team, ${badge} pending join request${badge === 1 ? '' : 's'}`
            : undefined
        }
      >
        <Flex align="center" gap={1} lineHeight={1.15}>
          <Text as="span" fontSize="sm">
            Team
          </Text>
          <Icon as={MdExpandMore} boxSize={3.5} opacity={0.75} pointerEvents="none" aria-hidden />
        </Flex>
        {badge > 0 ? <MobileFilterFabBadge count={badge} tone="notice" /> : null}
      </RiftboundButton>

      <NavSubmenuPortal
        open={hover.open}
        anchorRef={anchorRef}
        label="Team submenu"
        minW="240px"
        onMouseEnter={hover.show}
        onMouseLeave={hover.hide}
      >
          <Box
            bg={dropdownBg}
            borderWidth="hairline"
            borderColor="border"
            rounded="md"
            py={1}
            boxShadow={raisedShadow}
          >
            {teamSlug ? (
              <MyTeamSubmenuLink
                to={`/teams/${teamSlug}`}
                name={team?.name ?? 'My team'}
                slug={teamSlug}
                avatarUrl={team?.avatarUrl ?? null}
                active={myTeamActive}
              />
            ) : null}
            {teamSlug && canManage ? (
              <TeamSubmenuLink
                to={
                  badge > 0
                    ? `/teams/${teamSlug}?modal=members`
                    : `/teams/${teamSlug}?modal=settings`
                }
                icon={MdSettings}
                active={false}
              >
                Manage team
              </TeamSubmenuLink>
            ) : null}
            <TeamSubmenuLink to="/team" icon={MdSearch} active={hubActive}>
              Teams
            </TeamSubmenuLink>
            {!teamSlug ? (
              <TeamSubmenuLink to="/team?create=1" icon={MdAdd} active={false}>
                Create team
              </TeamSubmenuLink>
            ) : null}
            <TeamSubmenuLink to="/features/teams" icon={MdInfoOutline} active={landingActive}>
              Team features
            </TeamSubmenuLink>
          </Box>
      </NavSubmenuPortal>
    </Box>
  );
}
